/**
 * MERGE BRANDS PAGE (Admin Tool)
 * 
 * Merge duplicate/misspelled brands into the correct one.
 * Updates ALL items across ALL users and cleans up brand documents.
 * 
 * REQUIRES: isAdmin: true on your user document in Firestore
 * 
 * SETUP: Add route to App.tsx:
 * <Route path="/admin/merge-brands" element={<MergeBrands />} />
 * 
 * Supports URL params: ?old=OldBrand&new=NewBrand
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getDocs, doc, setDoc, getDoc, deleteDoc, collectionGroup, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft } from 'lucide-react';

export default function MergeBrands() {
  const [searchParams] = useSearchParams();
  const [oldBrand, setOldBrand] = useState(searchParams.get('old') || 'PrettyFingersbyRobin');
  const [newBrand, setNewBrand] = useState(searchParams.get('new') || 'Pretty Fingers by Robin');
  const [status, setStatus] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Update fields if URL params change
  useEffect(() => {
    const oldParam = searchParams.get('old');
    const newParam = searchParams.get('new');
    if (oldParam) setOldBrand(oldParam);
    if (newParam) setNewBrand(newParam);
  }, [searchParams]);

  // Check admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      
      if (user) {
        // Check if user is admin
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data()?.isAdmin === true);
        }
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsubscribe();
  }, []);

  const addLog = (message: string) => {
    setResults(prev => [...prev, message]);
    console.log(message);
  };

  // Helper: Create URL-friendly slug. Mirrors src/utils/polishSlug.ts:slugify —
  // normalize+strip accents so "Pénélope" → "penelope" instead of "pnlope".
  // Also converts "&" → " and " so "Lantern & Wren" → "lantern-and-wren".
  function createBrandSlug(brandName: string): string {
    return brandName
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  const mergeBrands = async () => {
    if (!oldBrand.trim() || !newBrand.trim()) {
      setStatus('Please enter both brand names');
      return;
    }

    if (oldBrand.trim() === newBrand.trim()) {
      setStatus('Brand names are the same!');
      return;
    }

    if (!currentUser || !isAdmin) {
      setStatus('You must be logged in as an admin!');
      return;
    }

    setIsRunning(true);
    setResults([]);
    
    const oldSlug = createBrandSlug(oldBrand);
    const newSlug = createBrandSlug(newBrand);

    addLog(`🔄 Merging "${oldBrand}" → "${newBrand}"`);
    addLog(`   Old slug: ${oldSlug}`);
    addLog(`   New slug: ${newSlug}`);
    addLog('');

    try {
      // Step 1: Find ALL items with the old brand name
      setStatus('Finding all items with old brand name...');
      addLog('📦 Scanning ALL items across ALL users...');

      const itemsSnapshot = await getDocs(collectionGroup(db, 'items'));
      
      const itemsToUpdate: { ref: any; data: any }[] = [];
      
      itemsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.brand === oldBrand || data.brand === oldBrand.trim()) {
          itemsToUpdate.push({ ref: docSnap.ref, data });
        }
      });

      addLog(`Found ${itemsToUpdate.length} items to update`);

      if (itemsToUpdate.length === 0) {
        addLog('⚠️ No items found with that brand name. Check spelling?');
        setStatus('No items found');
        setIsRunning(false);
        return;
      }

      // Step 2: Update all items in batches (Firestore limit: 500 per batch)
      setStatus(`Updating ${itemsToUpdate.length} items...`);
      addLog('');
      addLog('✏️ Updating items...');

      let updated = 0;
      let batchCount = 0;
      let batch = writeBatch(db);

      for (const item of itemsToUpdate) {
        batch.update(item.ref, { brand: newBrand.trim() });
        batchCount++;
        
        // Commit every 450 (staying under 500 limit)
        if (batchCount >= 450) {
          await batch.commit();
          updated += batchCount;
          addLog(`  Updated ${updated} items...`);
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      // Commit remaining
      if (batchCount > 0) {
        await batch.commit();
        updated += batchCount;
      }

      addLog(`✅ Updated ${updated} items`);

      // Step 3: Delete old brand document
      setStatus('Cleaning up old brand...');
      addLog('');
      addLog('🗑️ Deleting old brand document...');

      const oldBrandRef = doc(db, 'brands', oldSlug);
      const oldBrandDoc = await getDoc(oldBrandRef);
      
      if (oldBrandDoc.exists()) {
        await deleteDoc(oldBrandRef);
        addLog(`✅ Deleted brand document: ${oldSlug}`);
      } else {
        addLog(`⚠️ Old brand document not found (may already be deleted)`);
      }

      // Step 4: Update stats on new brand
      setStatus('Updating new brand stats...');
      addLog('');
      addLog('📊 Recalculating stats for new brand...');

      // Recount everything for the new brand
      const allItemsSnapshot = await getDocs(collectionGroup(db, 'items'));
      
      let itemCount = 0;
      const collectors = new Set<string>();
      const wishlisted = new Set<string>();
      const collections = new Set<string>();

      allItemsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.brand === newBrand.trim()) {
          itemCount++;
          const userId = docSnap.ref.parent.parent?.id;
          if (userId) {
            if (data.onWishlist) {
              wishlisted.add(userId);
            } else {
              collectors.add(userId);
            }
          }
          if (data.collectionName || data.collection) {
            collections.add(data.collectionName || data.collection);
          }
        }
      });

      const newBrandRef = doc(db, 'brands', newSlug);
      const newBrandDoc = await getDoc(newBrandRef);

      if (newBrandDoc.exists()) {
        // Update existing
        await setDoc(newBrandRef, {
          stats: {
            totalItems: itemCount,
            totalCollectors: collectors.size,
            totalWishlisted: wishlisted.size,
            collectionsCount: collections.size,
            lastUpdated: new Date()
          },
          updatedAt: new Date()
        }, { merge: true });
      } else {
        // Create new brand doc
        await setDoc(newBrandRef, {
          name: newBrand.trim(),
          slug: newSlug,
          nameLower: newBrand.toLowerCase().trim(),
          status: 'community',
          stats: {
            totalItems: itemCount,
            totalCollectors: collectors.size,
            totalWishlisted: wishlisted.size,
            collectionsCount: collections.size,
            lastUpdated: new Date()
          },
          followerCount: 0,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      addLog(`✅ New brand stats: ${itemCount} items, ${collectors.size} collectors`);

      // Done!
      addLog('');
      addLog('='.repeat(50));
      addLog('🎉 MERGE COMPLETE!');
      addLog('='.repeat(50));
      addLog(`Merged ${updated} items from "${oldBrand}" → "${newBrand}"`);
      
      setStatus(`Done! Merged ${updated} items.`);

    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
      setStatus('Error occurred - check logs');
      console.error(error);
    }

    setIsRunning(false);
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <p className="text-glass-secondary">Checking admin status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      {/* Back to Admin Dashboard */}
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-4 font-body"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Admin Dashboard
      </Link>

      <div className="glass-card p-6">
        <h1 className="text-2xl font-bold text-glass mb-2 font-heading">🔄 Merge Brands (Admin)</h1>
        <p className="text-glass-secondary mb-4 font-body">
          Merge a misspelled or duplicate brand into the correct one. This will update ALL items across ALL users.
        </p>

        {/* Admin Status */}
        {!currentUser ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">⚠️ You must be logged in to use this tool!</p>
          </div>
        ) : !isAdmin ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">⚠️ You must be an admin to use this tool!</p>
            <p className="text-red-400 text-sm mt-1">Add <code className="bg-slate-700 px-1 rounded">isAdmin: true</code> to your user document in Firestore.</p>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
            <p className="text-green-300">✅ Admin access confirmed - you can merge brands across all users</p>
          </div>
        )}

        {/* Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
              Old Brand Name (to merge FROM)
            </label>
            <input
              type="text"
              value={oldBrand}
              onChange={(e) => setOldBrand(e.target.value)}
              className="input-glass"
              placeholder="PrettyFingersbyRobin"
            />
            <p className="text-xs text-glass-muted mt-1">This brand will be deleted</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
              New Brand Name (to merge TO)
            </label>
            <input
              type="text"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              className="input-glass"
              placeholder="Pretty Fingers by Robin"
            />
            <p className="text-xs text-glass-muted mt-1">All items will be updated to this name</p>
          </div>
        </div>

        <div className="mb-6">
          <button
            onClick={mergeBrands}
            disabled={isRunning || !isAdmin}
            className={`px-6 py-3 rounded-lg font-medium text-white transition-colors font-body ${
              isRunning || !isAdmin
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            {isRunning ? 'Merging...' : '🔄 Merge Brands'}
          </button>
        </div>

        {/* Status */}
        {status && (
          <div className="mb-4 p-3 bg-slate-700/40 rounded-lg">
            <span className="font-medium text-glass">Status:</span> <span className="text-glass-secondary">{status}</span>
          </div>
        )}

        {/* Logs */}
        {results.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-glass mb-3 font-body">📋 Log</h2>
            <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm max-h-64 overflow-y-auto border border-slate-700">
              {results.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        )}

        {/* Warning */}
        <div className="mt-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
          <p className="text-red-300 text-sm font-body">
            ⚠️ <strong className="text-red-200">Warning:</strong> This action cannot be undone! This will update items for ALL users. Make sure the brand names are correct before merging.
          </p>
        </div>
      </div>
    </div>
  );
}
